import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { getCurrentUser } from "@/lib/auth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Shield, Users, Building2, Plus, Trash2, UserPlus, Copy, Eye, Search } from "lucide-react";

interface Tenant {
  id: string;
  name: string;
  tenantCode: string;
  contactEmail: string;
  contactPhone?: string;
  isActive: boolean;
  createdAt: string;
}

interface User {
  id: string;
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
  tenantId: string;
  isActive: boolean;
  createdAt: string;
}

export default function AdminPanel() {
  const { toast } = useToast();
  const currentUser = getCurrentUser();
  const [showCreateTenant, setShowCreateTenant] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showTenantUsers, setShowTenantUsers] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [generatedPassword, setGeneratedPassword] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const [tenantForm, setTenantForm] = useState({ name: "", contactEmail: "", contactPhone: "" });
  const [userForm, setUserForm] = useState({
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    role: "mine",
    tenantId: ""
  });

  if (currentUser?.role !== 'super_admin') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <Shield className="h-6 w-6" />
              Access Denied
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p>You don't have permission to access the admin panel.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { data: tenants = [], isLoading: tenantsLoading } = useQuery<Tenant[]>({
    queryKey: ['/api/admin/tenants'],
  });

  const { data: users = [], isLoading: usersLoading } = useQuery<User[]>({
    queryKey: ['/api/admin/users'],
  });

  const createTenantMutation = useMutation({
    mutationFn: (data: typeof tenantForm) => apiRequest('POST', '/api/admin/tenants', data),
    onSuccess: async (response) => {
      const tenant = await response.json();
      toast({ title: "Success", description: `Tenant "${tenant.name}" created successfully` });
      setShowCreateTenant(false);
      setTenantForm({ name: "", contactEmail: "", contactPhone: "" });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/tenants'] });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to create tenant", variant: "destructive" });
    }
  });

  const createUserMutation = useMutation({
    mutationFn: (data: typeof userForm) => apiRequest('POST', '/api/admin/users', data),
    onSuccess: async (response) => {
      const user = await response.json();
      toast({ title: "Success", description: `User created: ${user.email}` });
      const wasFromTenant = !!selectedTenant;
      setShowCreateUser(false);
      setUserForm({ email: "", password: "", firstName: "", lastName: "", role: "mine", tenantId: "" });
      setGeneratedPassword("");
      queryClient.invalidateQueries({ queryKey: ['/api/admin/users'] });
      
      if (wasFromTenant) {
        setTimeout(() => setShowTenantUsers(true), 100);
      }
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to create user", variant: "destructive" });
    }
  });

  const disableTenantMutation = useMutation({
    mutationFn: (tenantId: string) => apiRequest('DELETE', `/api/admin/tenants/${tenantId}`, {}),
    onSuccess: () => {
      toast({ title: "Success", description: "Tenant disabled successfully" });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/tenants'] });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to disable tenant", variant: "destructive" });
    }
  });

  const deleteTenantMutation = useMutation({
    mutationFn: (tenantId: string) => apiRequest('DELETE', `/api/admin/tenants/${tenantId}/permanent`, {}),
    onSuccess: () => {
      toast({ title: "Success", description: "Tenant permanently deleted" });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/tenants'] });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to delete tenant", variant: "destructive" });
    }
  });

  const disableUserMutation = useMutation({
    mutationFn: (userId: string) => apiRequest('DELETE', `/api/admin/users/${userId}`, {}),
    onSuccess: () => {
      toast({ title: "Success", description: "User disabled successfully" });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/users'] });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to disable user", variant: "destructive" });
    }
  });

  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => apiRequest('DELETE', `/api/admin/users/${userId}/permanent`, {}),
    onSuccess: () => {
      toast({ title: "Success", description: "User permanently deleted" });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/users'] });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to delete user", variant: "destructive" });
    }
  });

  const generatePassword = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%";
    let password = "";
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setGeneratedPassword(password);
    setUserForm({ ...userForm, password });
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: "Copied!", description: `${label} copied to clipboard` });
  };

   const openTenantUsers = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setShowTenantUsers(true);
  };

   const openCreateUserForTenant = (tenant: Tenant) => {
        setSelectedTenant(tenant);
           setUserForm({
      ...userForm,
      tenantId: tenant.id,
          });
                            setShowCreateUser(true);
  };

     const tenantUsers = selectedTenant 
    ? users.filter(u => u.tenantId === selectedTenant.id)
    :  [];

                              //  so juyt tenants based on search term
  const filteredTenants = tenants.filter(tenant => {
    if (!searchTerm) return true;
    
    const search = searchTerm.toLowerCase();
    return (
      tenant.name.toLowerCase().includes(search) ||
      tenant.tenantCode?.toLowerCase().includes(search) ||
      tenant.contactEmail?.toLowerCase().includes(search) ||
      tenant.contactPhone?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Shield className="h-8 w-8 text-red-600" />
            Super Admin Panel
          </h1>
          <p className="text-muted-foreground">Manage tenants and user accounts</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>Tenant Organizations</CardTitle>
                <CardDescription>Manage client organizations and their users. Click on any tenant to view and manage users.</CardDescription>
              </div>
              <Button onClick={() => setShowCreateTenant(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Tenant
              </Button>
            </div>
            
            {/* Search Bar */}
            <div className="flex items-center gap-3">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, code, email, or phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              {searchTerm && (
                <>
                  <Badge variant="secondary" className="text-sm">
                    {filteredTenants.length} of {tenants.length}
                  </Badge>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setSearchTerm("")}
                  >
                    Clear
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {tenantsLoading ? (
            <div className="text-center py-8">Loading...</div>
          ) : filteredTenants.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Building2 className="h-12 w-12 mx-auto mb-3 opacity-20" />
              <p className="text-lg font-medium">No tenants found</p>
              <p className="text-sm">
                {searchTerm 
                  ? `No results match "${searchTerm}". Try a different search term.`
                  : "Create your first tenant organization to get started."
                }
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tenant Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Contact Email</TableHead>
                  <TableHead>Contact Phone</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Users</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTenants.map((tenant) => {
                  const tenantUserCount = users.filter(u => u.tenantId === tenant.id).length;
                  return (
                    <TableRow key={tenant.id} className="cursor-pointer hover:bg-muted/50" onClick={() => openTenantUsers(tenant)}>
                      <TableCell className="font-mono font-semibold">{tenant.tenantCode}</TableCell>
                      <TableCell className="font-medium">{tenant.name}</TableCell>
                      <TableCell>{tenant.contactEmail}</TableCell>
                      <TableCell>{tenant.contactPhone || '-'}</TableCell>
                      <TableCell>
                        <Badge variant={tenant.isActive ? "default" : "secondary"}>
                          {tenant.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="cursor-pointer">
                          <Users className="h-3 w-3 mr-1" />
                          {tenantUserCount}
                        </Badge>
                      </TableCell>
                      <TableCell>{new Date(tenant.createdAt).toLocaleDateString()}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => openTenantUsers(tenant)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          {tenant.id !== 'super-admin-tenant' && (
                            <>
                              {tenant.isActive && (
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button variant="outline" size="sm" className="text-orange-600 hover:text-orange-700">
                                      Disable
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Disable Tenant?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        This will disable "{tenant.name}" and all its users will lose access. This action can be reversed by reactivating the tenant in the database.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                                      <AlertDialogAction 
                                        onClick={() => disableTenantMutation.mutate(tenant.id)}
                                        className="bg-orange-600 hover:bg-orange-700"
                                      >
                                        Disable
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              )}
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="outline" size="sm">
                                    <Trash2 className="h-4 w-4 text-red-600" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Permanently Delete Tenant?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This will PERMANENTLY DELETE "{tenant.name}" and all associated data. This action CANNOT be reversed!
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction 
                                      onClick={() => deleteTenantMutation.mutate(tenant.id)}
                                      className="bg-red-600 hover:bg-red-700"
                                    >
                                      Permanently Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

        <Dialog open={showCreateTenant} onOpenChange={setShowCreateTenant}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Tenant</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Organization Name *</Label>
              <Input
                value={tenantForm.name}
                onChange={(e) => setTenantForm({ ...tenantForm, name: e.target.value })}
                placeholder="e.g., ABC Mining Corp"
              />
            </div>
            <div>
              <Label>Contact Email *</Label>
              <Input
                type="email"
                value={tenantForm.contactEmail}
                onChange={(e) => setTenantForm({ ...tenantForm, contactEmail: e.target.value })}
                placeholder="contact@company.com"
              />
            </div>
            <div>
              <Label>Contact Phone</Label>
              <Input
                value={tenantForm.contactPhone}
                onChange={(e) => setTenantForm({ ...tenantForm, contactPhone: e.target.value })}
                placeholder="+1234567890"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCreateTenant(false)}>Cancel</Button>
              <Button onClick={() => createTenantMutation.mutate(tenantForm)} disabled={createTenantMutation.isPending}>
                {createTenantMutation.isPending ? "Creating..." : "Create Tenant"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {selectedTenant ? `Create New User for ${selectedTenant.name}` : "Create New User"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name</Label>
                <Input
                  value={userForm.firstName}
                  onChange={(e) => setUserForm({ ...userForm, firstName: e.target.value })}
                  placeholder="John"
                />
              </div>
              <div>
                <Label>Last Name</Label>
                <Input
                  value={userForm.lastName}
                  onChange={(e) => setUserForm({ ...userForm, lastName: e.target.value })}
                  placeholder="Doe"
                />
              </div>
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                placeholder="user@company.com"
              />
            </div>
            <div>
              <Label>Password *</Label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  placeholder="Enter password"
                />
                <Button type="button" variant="outline" onClick={generatePassword}>
                  Generate
                </Button>
                {userForm.password && (
                  <Button type="button" variant="outline" size="icon" onClick={() => copyToClipboard(userForm.password, "Password")}>
                    <Copy className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {generatedPassword && (
                <p className="text-sm text-orange-600 mt-1">⚠️ Save this password! It won't be shown again.</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tenant *</Label>
                <Select value={userForm.tenantId} onValueChange={(value) => setUserForm({ ...userForm, tenantId: value })} disabled={!!selectedTenant}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select tenant" />
                  </SelectTrigger>
                  <SelectContent>
                    {tenants.filter(t => t.isActive).map((tenant) => (
                      <SelectItem key={tenant.id} value={tenant.id}>
                        {tenant.name} ({tenant.tenantCode})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type *</Label>
                <Select value={userForm.role} onValueChange={(value) => setUserForm({ ...userForm, role: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mine">Mine</SelectItem>
                    <SelectItem value="port">Port</SelectItem>
                    <SelectItem value="staging">Staging</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {userForm.email && userForm.password && userForm.tenantId && (
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg space-y-2">
                <h4 className="font-semibold text-sm">📧 Send these credentials to the user:</h4>
                <div className="space-y-1 text-sm font-mono">
                  <div className="flex justify-between items-center">
                    <span>Email: {userForm.email}</span>
                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(userForm.email, "Email")}>
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Password: {userForm.password}</span>
                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(userForm.password, "Password")}>
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                  <div>Tenant: {tenants.find(t => t.id === userForm.tenantId)?.name}</div>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => {
                setShowCreateUser(false);
                if (selectedTenant) {
                  setShowTenantUsers(true);
                }
              }}>Cancel</Button>
              <Button 
                onClick={() => createUserMutation.mutate(userForm)} 
                disabled={createUserMutation.isPending || !userForm.email || !userForm.password || !userForm.tenantId}
              >
                {createUserMutation.isPending ? "Creating..." : "Create User"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showTenantUsers} onOpenChange={(open) => {
        setShowTenantUsers(open);
        if (!open) {
          setSelectedTenant(null);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  {selectedTenant?.name}
                </DialogTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  {selectedTenant?.tenantCode} • {selectedTenant?.contactEmail}
                </p>
              </div>
              <Button onClick={() => {
                if (selectedTenant) {
                  setShowTenantUsers(false);
                  openCreateUserForTenant(selectedTenant);
                }
              }}>
                <UserPlus className="h-4 w-4 mr-2" />
                Add Partner
              </Button>
            </div>
          </DialogHeader>
          
          <div className="mt-4">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <Users className="h-4 w-4" />
              Users ({tenantUsers.length})
            </h3>
            
            {tenantUsers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-2 opacity-20" />
                <p>No users found for this tenant</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => {
                    if (selectedTenant) {
                      setShowTenantUsers(false);
                      openCreateUserForTenant(selectedTenant);
                    }
                  }}
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add First Partner
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tenantUsers.map((user) => {
                    const isSelf = user.id === currentUser?.id;
                    return (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.email}</TableCell>
                        <TableCell>
                          {user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge variant={user.role === 'super_admin' ? 'destructive' : 'default'}>
                            {user.role === 'super_admin' ? 'Super Admin' : user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={user.isActive ? "default" : "secondary"}>
                            {user.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          {new Date(user.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {!isSelf && (
                            <div className="flex gap-2">
                              {user.isActive && (
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button variant="outline" size="sm" className="text-orange-600 hover:text-orange-700">
                                      Disable
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Disable User?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        This will disable "{user.email}" and they will lose access to the system. This action can be reversed by reactivating the user in the database.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                                      <AlertDialogAction 
                                        onClick={() => disableUserMutation.mutate(user.id)}
                                        className="bg-orange-600 hover:bg-orange-700"
                                      >
                                        Disable
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              )}
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="outline" size="sm">
                                    <Trash2 className="h-4 w-4 text-red-600" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Permanently Delete User?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This will PERMANENTLY DELETE "{user.email}" and all associated data. This action CANNOT be reversed!
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction 
                                      onClick={() => deleteUserMutation.mutate(user.id)}
                                      className="bg-red-600 hover:bg-red-700"
                                    >
                                      Permanently Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          )}
                          {isSelf && (
                            <span className="text-xs text-muted-foreground">Current User</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


